import { useEffect, useRef } from 'react';
import { motion, useInView, animate } from 'framer-motion';
import { Button } from '@educa-legal/ui';

const stats = [
  { prefix: '+', numeric: 20,  suffix: '', label: 'Anos de experiência jurídica do fundador',           color: 'text-orange' },
  { prefix: '+', numeric: 100, suffix: '', label: 'Instituições assessoradas em todo o Brasil',          color: 'text-sky' },
  { prefix: '',  numeric: 11,  suffix: '', label: 'Estados de atuação nacional',                         color: 'text-olive' },
  { prefix: '+', numeric: 11,  suffix: '', label: 'Anos dedicados exclusivamente ao setor educacional',  color: 'text-mustard' },
];

const clients = [
  'Arco Educação',
  'Somos Educação',
  'EB Educação',
  'Bioma Educação',
  'Dema Jovem by Renapsi',
  'Escola da Vila',
  'Escola Internacional de Alphaville',
  'Brazilian International School',
  'Instituto Logosófico de São Paulo',
  'Isaac',
  'Balão Vermelho',
  'Escola Parque',
  'Centro Viva',
  'Fórum Cultural',
  'Colégio Apoio',
  'Escola Autonomia',
  'Dual International School',
  'Escola Mais',
  'Casa da Gente',
  'Colégio João de Barro',
  'Colégio Santa Moniquinha',
  'Colégio Augustus',
  'Colégio Miguel Afonso',
  'Escola BEK',
  'Faculdade da Criança',
];

const row1 = clients.slice(0, 13);
const row2 = clients.slice(13);

function AnimatedNumber({
  prefix,
  numeric,
  suffix,
  color,
  delay = 0,
}: {
  prefix: string;
  numeric: number;
  suffix: string;
  color: string;
  delay?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView || !ref.current) return;
    const el = ref.current;
    const controls = animate(0, numeric, {
      duration: 1.6,
      delay,
      ease: 'easeOut',
      onUpdate(v) {
        el.textContent = `${prefix}${Math.round(v)}${suffix}`;
      },
    });
    return () => controls.stop();
  }, [isInView, prefix, numeric, suffix, delay]);

  return (
    <span ref={ref} className={color}>
      {prefix}0{suffix}
    </span>
  );
}

function Pill({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center px-5 py-2.5 rounded-xl border border-neutral-200 bg-white text-neutral-500 text-sm font-semibold tracking-wide whitespace-nowrap select-none">
      {name}
    </span>
  );
}

function MarqueeRow({
  items,
  reverse = false,
  duration = 38,
}: {
  items: string[];
  reverse?: boolean;
  duration?: number;
}) {
  const doubled = [...items, ...items];
  const animClass = reverse ? 'animate-marquee-right' : 'animate-marquee-left';

  return (
    <div className="marquee-track relative overflow-hidden py-1.5">
      <div
        className={`flex gap-4 w-max ${animClass}`}
        style={{ '--marquee-duration': `${duration}s` } as React.CSSProperties}
      >
        {doubled.map((name, i) => (
          <Pill key={`${name}-${i}`} name={name} />
        ))}
      </div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent z-10" />
    </div>
  );
}

export function SocialProof() {
  return (
    <section className="py-16 md:py-20 bg-white overflow-hidden">
      <style>{`
        @keyframes marquee-left {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          from { transform: translateX(-50%); }
          to   { transform: translateX(0); }
        }
        .animate-marquee-left {
          animation: marquee-left var(--marquee-duration, 38s) linear infinite;
        }
        .animate-marquee-right {
          animation: marquee-right var(--marquee-duration, 44s) linear infinite;
        }
        .marquee-track:hover .animate-marquee-left,
        .marquee-track:hover .animate-marquee-right {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-marquee-left,
          .animate-marquee-right {
            animation: none;
          }
        }
      `}</style>

      <div className="section-container">
        {/* Números */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              className="text-center"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-48px' }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <div className="text-display-md md:text-display-lg font-bold mb-2">
                <AnimatedNumber
                  prefix={stat.prefix}
                  numeric={stat.numeric}
                  suffix={stat.suffix}
                  color={stat.color}
                  delay={i * 0.08}
                />
              </div>
              <p className="text-body-sm text-neutral-500 leading-snug">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Texto de transição */}
        <motion.div
          className="mt-14 text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-48px' }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-display-sm md:text-display-md font-bold text-neutral-950 mb-3">
            Grandes nomes do setor educacional brasileiro já confiam na Educa Legal.
          </h2>
          <p className="text-body-md text-neutral-500 max-w-2xl mx-auto">
            De escolas independentes a grandes redes nacionais — atendemos instituições de todos os
            portes em 11 estados do país.
          </p>
        </motion.div>
      </div>

      {/* Logos marquee (full bleed) */}
      <motion.div
        className="mt-10 flex flex-col gap-3"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-48px' }}
        transition={{ duration: 0.5 }}
      >
        <MarqueeRow items={row1} reverse={false} duration={38} />
        <MarqueeRow items={row2} reverse={true} duration={44} />
      </motion.div>

      {/* CTA final */}
      <div className="section-container">
        <motion.div
          className="mt-10 text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-body-md text-neutral-500 mb-4">Sua instituição pode ser a próxima.</p>
          <Button asChild variant="primary" size="lg">
            <a href="https://wa.me/5511989082892" target="_blank" rel="noopener noreferrer">
              Fale com a Educa Legal
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
